import React from 'react';
import { FormattedMessage } from 'react-intl';
import FormattedCurrencyNumber from 'common/formatted-currency-number/formatted-currency-number';
import PropTypes from 'prop-types';
import { SubTitle, Div, Flex, Icon, Span } from 'ui';
import withLoading from 'common/spinner/components/with-loading';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { orderStatus } from 'utils/app-configs';
import SubcontractList from './subcontract-list';

const Wrapper = styled(Flex)`
  color: #9b9b9b;
  margin-left: 30px;
`;

const ToolbarItem = styled(Div)`
  padding-left: 10px;
  padding-right: 10px;
`;

const SeverityIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[2]};
  `;

const Text = styled.div`
margin-left: 15px;
margin-top: 5px;
color: ${p => p.theme.colors.alerts[2]};
  font-weight: ${p => p.theme.fontWeight.bolder};
`;

const Subcontracts = ({
  activeStatus,
  subcontracts,
  form,
  disabled,
  shipTo,
  vendors,
  isVendorsLoading,
  setFormDirty,
}) => {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const getWarnings = () => {
    const subcos = getFieldValue('subContracts');

    // in draft with subcontracts, always show.
    if (activeStatus && activeStatus.id === orderStatus.draft) {
      if (subcos && subcos.length) {
        return <FormattedMessage id='order.subcontracts.SUBCO_SAVE_CREATED_WARNING' />;
      }
    }
    // in created, only with new subcontracts, show.
    if (activeStatus && activeStatus.id === orderStatus.created) {
      if (subcos && subcos?.filter(s => !s.isFrozen).length) {
        return <FormattedMessage id='order.subcontracts.SUBCO_SAVE_CREATED_WARNING' />;
      }
    }

    return null;
  };

  const addSubContract = (subcontract) => {
    const subcoToAdd = {
      id: uuidv4(),
      itemType: null,
      itemTypeName: null,
      itemDescription: null,
      cost: null,
      costCurrency: null,
      costCurrencyName: null,
      vendor: null,
      vendorName: null,
      poNumber: null,
      vendorInvoiceIndicator: null,
      goodsReceiptIndicator: null,
      changeIndicator: 0,
      isFrozen: false,
      ...subcontract,
    };

    const subcos = getFieldValue('subContracts');
    setFieldsValue({
      subContracts: subcos ? [...subcos, subcoToAdd] : [subcoToAdd],
    });
  };

  const removeSubContract = (subcontract) => {
    const subcos = getFieldValue('subContracts');
    setFieldsValue({
      subContracts: subcos?.filter(s => s.id !== subcontract.id),
    });
  };

  const calculateTotalCost = () => {
    const subcos = getFieldValue('subContracts') || [];
    const cost = subcos.reduce((all, subco) => {
      if (all[subco.costCurrency || 'unknown']) {
        all[subco.costCurrency || 'unknown'] += parseFloat(subco.cost);
      } else {
        all[subco.costCurrency || 'unknown'] = parseFloat(subco.cost);
      }
      return all;
    }, {});

    return (
      <Wrapper>
        <ToolbarItem>
          <FormattedMessage id='order.parts.TOTAL_COST' />
        </ToolbarItem>
        {cost && Object.keys(cost).length
          ? Object.keys(cost)
            ?.map(c =>
              <ToolbarItem key={c}>
                <FormattedCurrencyNumber value={cost[c]} />
                {` ${c && c}`}
              </ToolbarItem>,
            )
            .reduce((prev, curr) => prev === null ? [curr] : [prev, ' + ', curr], null)
          : 0}
      </Wrapper>
    );
  };

  const inactive = disabled || (activeStatus && activeStatus.id > orderStatus.adminClosed);

  getFieldDecorator('subContracts', {
    initialValue: subcontracts || [],
  });

  const subContractsValue = getFieldValue('subContracts');
  const warnings = getWarnings();

  return (
    <Div marginTop={45}>
      <SubTitle>
        <FormattedMessage id='common.titles.SUBCONTRACTING' />
        {calculateTotalCost()}
        {warnings &&
          <Text>
            <SeverityIcon type='exclamation-triangle' />
            <Span marginLeft={5}>{warnings}</Span>
          </Text>
        }
      </SubTitle>
      <SubcontractList
        data={subContractsValue}
        disabled={inactive}
        addSubContract={addSubContract}
        removeSubContract={removeSubContract}
        shipTo={shipTo}
        vendors={vendors}
        isVendorsLoading={isVendorsLoading}
        setFormDirty={setFormDirty}
      />
    </Div>
  );
};

Subcontracts.propTypes = {
  activeStatus: PropTypes.object,
  subcontracts: PropTypes.array,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  shipTo: PropTypes.object,
  vendors: PropTypes.array,
  isVendorsLoading: PropTypes.bool,
  setFormDirty: PropTypes.func,
};

export default withLoading(Subcontracts, 'common.LOADING_SUBCONTRACTS', true);
